/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, ChevronDown, Map as MapIcon, Smartphone, LogOut, BookOpen, Newspaper, Grape, MapPin, Sparkles, Compass, Layers, Landmark, Book, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import SearchBar from "@/components/SearchBar";
import CommandBarTrigger from "@/components/CommandBar";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";

const LOGO = "https://customer-assets.emergentagent.com/job_e33c4909-1cdb-40b6-bbfc-eef1964c596a/artifacts/qhav103f_geoVINUM_logo_RVB_300ppi_transparent.png";
const APP_URL = "https://app.geovinum.io";

// Top-level menu items — 4 pillars + Explorer + Pros.
// Every label goes through t() so the whole nav mirrors the active
// language. Do NOT hardcode any FR/EN string here.
const MENU = (t) => [
  {
    key: "maps",
    label: t("nav.maps"),
    to: "/shop",
  },
  {
    key: "explorer",
    label: t("nav.explore"),
    to: "/blog",
    // Sub-menu items land on `/blog?cat=…` — the unified explorer reads
    // this URL param, filters the merged index and pre-selects the
    // matching chip in its filter bar, so a Cépages click drops the
    // visitor straight in a filtered view instead of the mixed index.
    sub: [
      { slug: "all",         to: "/blog",                 label: t("nav.explorer.all"),          Icon: Sparkles },
      { slug: "appellations",to: "/blog?cat=appellation", label: t("nav.explorer.appellations"), Icon: MapPin },
      { slug: "cepages",     to: "/blog?cat=cepage",      label: t("nav.explorer.cepages"),      Icon: Grape },
      { slug: "producers",   to: "/blog?cat=producer",    label: t("nav.explorer.producers"),    Icon: BookOpen },
      { slug: "articles",    to: "/blog?cat=wine_insights", label: t("nav.explorer.articles"),   Icon: Newspaper },
      // GEO/SEO pillars + glossary — accessible directly from the top
      // menu so operators AND search engines see the internal maillage
      // to our high-value SEO pages.
      { slug: "sep-guides",  divider: t("nav.explorer.guides_divider") },
      { slug: "glossaire",         to: "/glossaire",              label: t("nav.explorer.glossaire"),         Icon: Book },
      { slug: "cartographie",      to: "/cartographie-viticole",  label: t("nav.explorer.cartographie"),      Icon: MapIcon },
      { slug: "geographie",        to: "/geographie-du-vin",      label: t("nav.explorer.geographie"),        Icon: Compass },
      { slug: "appellations-pil",  to: "/appellations-viticoles", label: t("nav.explorer.appellations_pil"), Icon: Landmark },
      { slug: "terroirs",          to: "/terroirs-viticoles",     label: t("nav.explorer.terroirs"),          Icon: Layers },
    ],
  },
  {
    key: "app",
    label: t("nav.app"),
    to: "/app",
  },
  {
    key: "pro",
    label: t("nav.pro_long"),
    to: "/pro",
    pink: true,
    // The Pro dropdown groups every B2B surface: overview page,
    // the embeddable Interactive product and the Experience landing
    // (event/adventure creator). Keeps top-level nav short and
    // dedicated to visitors while surfacing all pro products in
    // one place for operators.
    sub: [
      { slug: "overview",    to: "/pro",              label: t("nav.pro.overview"),    Icon: Building2 },
      { slug: "interactive", to: "/products/iframe",  label: t("nav.interactive"),     Icon: Layers },
      { slug: "experience",  to: "/experience",       label: t("nav.experience"),      Icon: Sparkles },
    ],
  },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const dropRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();

  useEffect(() => { setOpenKey(null); setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!openKey) return;
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openKey]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = MENU(t);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-lg border-b border-[color:var(--gv-line)]" : "bg-white/80 backdrop-blur-sm"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 xl:px-10 h-[72px] flex items-center gap-3 lg:gap-4 xl:gap-6">
        <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo-link">
          <img src={LOGO} alt="geoVINUM" className="h-9 w-auto" draggable="false" />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-7 shrink-0" ref={dropRef}>
          {items.map((item) => {
            const active = item.to && location.pathname === item.to;
            if (!item.sub) {
              // External link (opens in a new tab)
              if (item.external) {
                return (
                  <a
                    key={item.key}
                    href={item.to}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-[14px] tracking-wide inline-flex items-center gap-1 ${item.pink ? "text-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink-dark,#C01661)]" : "text-[color:var(--gv-ink)]/80 hover:text-[color:var(--gv-pink)]"}`}
                    data-testid={`nav-${item.key}`}
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  className={({ isActive }) =>
                    `text-[14px] tracking-wide ${isActive ? "text-[color:var(--gv-pink)]" : item.pink ? "text-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink-dark,#C01661)]" : "text-[color:var(--gv-ink)]/80 hover:text-[color:var(--gv-pink)]"}`
                  }
                  data-testid={`nav-${item.key}`}
                >
                  {item.label}
                </NavLink>
              );
            }
            return (
              <div key={item.key} className="relative">
                <button
                  onClick={() => setOpenKey(openKey === item.key ? null : item.key)}
                  data-testid={`nav-${item.key}-toggle`}
                  className={`inline-flex items-center gap-1.5 text-[14px] tracking-wide transition-colors ${active || openKey === item.key ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/80 hover:text-[color:var(--gv-pink)]"}`}
                >
                  {item.label}
                  <ChevronDown size={13} className={`transition-transform ${openKey === item.key ? "rotate-180" : ""}`} />
                </button>
                {openKey === item.key && (
                  <div className="absolute left-0 top-[calc(100%+10px)] w-[280px] bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-[0_24px_60px_-20px_rgba(46,80,103,0.25)] py-2" data-testid={`nav-${item.key}-dropdown`}>
                    <Link to={item.to} className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[color:var(--gv-pink)] font-medium hover:bg-[color:var(--gv-mist)]">
                      {item.label} →
                    </Link>
                    <div className="my-1 mx-4 h-px bg-[color:var(--gv-line)]" />
                    {item.sub.map(({ slug, to, label, Icon, badge, divider }) => (
                      divider ? (
                        <div key={slug} className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.22em] text-[color:var(--gv-ink)]/40 border-t border-[color:var(--gv-line)] mt-1" data-testid={`nav-${item.key}-divider-${slug}`}>
                          {divider}
                        </div>
                      ) : (
                        <Link key={slug} to={to} className={`flex items-center gap-3 px-4 py-2.5 text-[13.5px] hover:bg-[color:var(--gv-mist)] ${location.pathname === to ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/85"}`} data-testid={`nav-${item.key}-${slug}`}>
                          <Icon size={15} className="text-[color:var(--gv-blue)]" />
                          <span className="flex-1">{label}</span>
                          {badge && <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/45">{badge}</span>}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="hidden xl:block flex-1 max-w-sm ml-auto">
          <CommandBarTrigger testId="navbar-cmdk" />
        </div>

        <div className="hidden md:flex items-center gap-1.5 xl:gap-2 shrink-0 ml-auto xl:ml-0">
          <CurrencySwitcher compact testIdPrefix="navbar-ccy" />
          <LanguageSwitcher compact testIdPrefix="navbar-lang" />
          {user ? (
            <div className="inline-flex items-center gap-1">
              <button onClick={() => navigate("/dashboard")} data-testid="navbar-dashboard-btn" className="inline-flex items-center gap-2 px-3 py-2.5 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors">
                <User size={14} /> {user.name || t("nav.account")}
              </button>
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="navbar-logout-btn"
                title={t("nav.logout")}
                aria-label={t("nav.logout")}
                className="w-9 h-9 inline-flex items-center justify-center rounded-[5px] border border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)] hover:border-[color:var(--gv-pink)] transition-colors"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            // Login is handled inside the app (app.geovinum.io) — the marketing
            // site simply routes visitors there instead of duplicating auth
            // screens. Removes the confusing "Iniciar sesión / Se connecter"
            // button and gives everyone a single CTA to enter the product.
            <a
              href={APP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="navbar-open-app"
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-[5px] bg-[color:var(--gv-ink)] text-white text-[13px] font-medium hover:bg-[color:var(--gv-blue)] transition-colors"
            >
              <Smartphone size={14} /> {t("nav.open_app")}
            </a>
          )}
        </div>

        <button className="lg:hidden ml-auto w-10 h-10 flex items-center justify-center" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[color:var(--gv-line)] px-6 py-5 space-y-4 max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="mobile-menu">
          <SearchBar testIdPrefix="mobile-search" />
          {items.map((item) => (
            <div key={item.key}>
              {item.external ? (
                <a
                  href={item.to}
                  target="_blank"
                  rel="noreferrer"
                  className={`block font-display text-[18px] mb-2 ${item.pink ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]"}`}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  to={item.to}
                  className={`block font-display text-[18px] mb-2 ${item.pink ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]"}`}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </Link>
              )}
              {item.sub && (
                <div className="grid grid-cols-2 gap-2 ml-1">
                  {item.sub.map(({ slug, to, label, Icon, divider }) => (
                    divider ? (
                      <div key={slug} className="col-span-2 px-1 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/45 border-t border-[color:var(--gv-line)]">
                        {divider}
                      </div>
                    ) : (
                      <Link key={slug} to={to} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)] hover:border-[color:var(--gv-pink)]" data-testid={`mobile-${item.key}-${slug}`}>
                        <Icon size={13} className="text-[color:var(--gv-blue)]" />
                        {label}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex flex-col gap-3 pt-2 border-t border-[color:var(--gv-line)]">
            <div className="flex gap-2">
              <LanguageSwitcher testIdPrefix="mobile-lang" />
              <CurrencySwitcher testIdPrefix="mobile-ccy" />
            </div>
            <a href={APP_URL} target="_blank" rel="noreferrer" className="btn-ghost w-full" data-testid="mobile-open-app"><Smartphone size={14} /> {t("nav.open_app")}</a>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-primary w-full"><User size={15} /> {t("nav.account")}</Link>
                <button
                  onClick={() => { logout(); setOpen(false); navigate("/"); }}
                  data-testid="navbar-logout-mobile"
                  className="btn-ghost w-full"
                ><LogOut size={15} /> {t("nav.logout")}</button>
              </>
            ) : (
              // Login lives inside the app itself — no login link on the
              // marketing site so visitors always end up in app.geovinum.io.
              <a href={APP_URL} target="_blank" rel="noreferrer" className="btn-primary w-full" data-testid="mobile-open-app-primary" onClick={() => setOpen(false)}><Smartphone size={15} /> {t("nav.open_app")}</a>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
